import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import ringApi, { getRingSession } from "@/lib/ringApi";
import { formatApiError } from "@/lib/api";
import { useRoom } from "@/hooks/useRoom";
import { useT } from "@/i18n";
import { computeRemaining, fmtClock } from "@/lib/timer";
import { playRoundEnd, playBreakEnd, primeAudio } from "@/lib/sfx";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Play, Pause, ArrowClockwise, Plus, Minus, FirstAid, Trophy, SignOut, MonitorPlay, SkipForward, WifiHigh, WifiSlash, CheckCircle, Circle, X, Microphone, MicrophoneSlash } from "@phosphor-icons/react";

const nameOf = (m, id) => {
  const a = m?.athletes?.[id];
  return a ? `${a.first_name} ${a.last_name}` : "—";
};

export default function RingControl() {
  const { ringId } = useParams();
  const nav = useNavigate();
  const t = useT();
  const session = getRingSession();
  const [queue, setQueue] = useState({ matches: [], athletes: {}, ring: null, pools: {} });
  const [remaining, setRemaining] = useState(0);
  const [showJudges, setShowJudges] = useState(true);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("ring_sound") !== "off");
  const toggleSound = () => setSoundOn((v) => { localStorage.setItem("ring_sound", v ? "off" : "on"); return !v; });
  const tick = useRef();
  const endedRef = useRef(null);

  const { snapshot, connected } = useRoom(session ? ringId : null, session?.token);
  const match = snapshot?.match || null;
  const judges = snapshot?.judges || [];
  const readyCount = snapshot?.ready_count || 0;
  const numJudges = match?.config?.num_judges || 0;

  const loadQueue = useCallback(async () => {
    const { data } = await ringApi.get(`/rings/${ringId}/queue`);
    setQueue(data);
  }, [ringId]);

  const selectMatch = async (m) => {
    await ringApi.post(`/rings/${ringId}/set-current`, { match_id: m.id });
  };

  const act = useCallback(async (action, payload) => {
    primeAudio();
    if (!match) return;
    try {
      await ringApi.post(`/matches/${match.id}/action`, { action, payload });
      if (action === "set_winner") { toast.success(t("rc.winnerDecreed")); loadQueue(); }
      if (action === "reset_match") { toast.success(t("rc.matchReset")); loadQueue(); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [match, t, loadQueue]);

  // Redirect to ring login if there is no valid session, otherwise load the queue.
  useEffect(() => {
    const s = getRingSession();
    if (!s || s.ring.id !== ringId) { nav(`/ring-login?ring_id=${ringId}`); return; }
    loadQueue();
  }, [ringId, nav, loadQueue]);

  useEffect(() => {
    clearInterval(tick.current);
    if (match?.discipline === "combat" && match?.state?.timer) {
      tick.current = setInterval(() => setRemaining(computeRemaining(match.state.timer)), 200);
    }
    return () => clearInterval(tick.current);
  }, [match]);

  // Auto-advance: when a running combat round/break clock hits zero, fire end_round once.
  useEffect(() => {
    const timer = match?.state?.timer;
    if (match?.discipline !== "combat" || !timer?.running || match.state.winner) return;
    if (computeRemaining(timer) > 0) return;
    if (endedRef.current === timer.updated_at) return;
    endedRef.current = timer.updated_at;
    if (soundOn) {
      if ((match.state.phase || "round") === "break") playBreakEnd();
      else playRoundEnd();
    }
    act("end_round");
  }, [remaining, match, act, soundOn]);

  const logout = () => { localStorage.removeItem("ring_session"); nav("/ring-login"); };
  const openMonitor = () => window.open(`/ring/${ringId}/monitor`, "_blank");

  const clearJudges = async () => {
    if (!window.confirm(t("rc.clearJudgesConfirm"))) return;
    try { await ringApi.delete(`/rings/${ringId}/judges`); toast.success(t("rc.judgesCleared")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetJudges = async () => {
    if (!window.confirm(t("rc.resetConfirm"))) return;
    try { await ringApi.post(`/rings/${ringId}/reset-judges`); toast.success(t("rc.judgesReset")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeJudge = async (jid) => {
    if (!window.confirm(t("rc.removeJudgeConfirm"))) return;
    try { await ringApi.delete(`/rings/${ringId}/judges/${jid}`); toast.success(t("rc.judgeRemoved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!session) return null;

  const requireJudges = match?.state?.require_judges ?? true;
  const canStart = !requireJudges || numJudges === 0 || readyCount >= numJudges;
  const judgeUrl = `${window.location.origin}/judge/${session.event.id}/${ringId}/${session.ring_password || ""}`;
  const running = match?.state?.timer?.running;

  return (
    <div className="min-h-screen bg-background text-white flex flex-col">
      <header className="h-14 border-b border-neutral-800 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <span className="font-heading font-extrabold text-brand-yellow">{session.ring.name}</span>
          {connected ? <WifiHigh className="text-medical" size={16} data-testid="ring-ws-online" /> : <WifiSlash className="text-warn" size={16} data-testid="ring-ws-offline" />}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-2 border-neutral-700" onClick={() => setShowJudges((s) => !s)} data-testid="toggle-judges">{t("rc.judges")}</Button>
          <Button size="sm" variant="outline" className="gap-2 border-neutral-700" onClick={openMonitor} data-testid="open-monitor-btn"><MonitorPlay /> {t("rc.monitor")}</Button>
          <Button size="sm" variant="ghost" onClick={logout} data-testid="ring-logout"><SignOut /></Button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <aside className="w-72 border-r border-neutral-800 overflow-y-auto shrink-0" data-testid="match-queue">
            <div className="p-3 text-xs uppercase tracking-widest text-neutral-500 font-bold">{t("rc.incontri")}</div>
            {queue.matches.length === 0 && <p className="px-3 text-sm text-neutral-500">{t("rc.noneAssigned")}</p>}
            {queue.matches.map((m, i) => {
              const prev = queue.matches[i - 1];
              const showHead = !prev || prev.pool_id !== m.pool_id;
              const poolName = queue.pools?.[m.pool_id]?.name;
              return (
                <React.Fragment key={m.id}>
                  {showHead && poolName && (
                    <div className="px-3 py-1.5 bg-neutral-900 border-b border-neutral-800 text-[10px] uppercase tracking-widest text-brand-yellow font-bold" data-testid={`queue-pool-head-${m.pool_id}`}>{poolName}</div>
                  )}
                  <button onClick={() => selectMatch(m)} data-testid={`queue-match-${m.id}`}
                    className={`w-full text-left px-3 py-3 border-b border-neutral-900 hover:bg-neutral-900 ${match?.id === m.id ? "bg-neutral-900" : ""}`}>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">{m.label} · {m.discipline === "combat" ? t("type.combat") : t("type.poomsae")}</div>
                    <div className="text-sm font-medium mt-0.5">
                      {m.discipline === "combat"
                        ? `${nameOf({ athletes: queue.athletes }, m.red_athlete_id)} vs ${nameOf({ athletes: queue.athletes }, m.blue_athlete_id)}`
                        : nameOf({ athletes: queue.athletes }, m.athlete_id || m.red_athlete_id)}
                    </div>
                    <div className="text-[10px] mt-1 text-neutral-500">{m.status}</div>
                  </button>
                </React.Fragment>
              );
            })}
          </aside>

        <main className="flex-1 min-h-0 overflow-y-auto">
          {!match ? (
            <div className="h-full flex items-center justify-center text-neutral-500">{t("rc.selectMatch")}</div>
          ) : match.discipline === "combat" ? (
            <CombatPanel match={match} remaining={remaining} running={running} act={act} canStart={canStart} readyCount={readyCount} numJudges={numJudges} requireJudges={requireJudges} soundOn={soundOn} onToggleSound={toggleSound} />
          ) : (
            <PoomsaePanel match={match} act={act} judges={judges} canStart={canStart} poomsaeVotes={snapshot?.poomsae_votes} />
          )}
        </main>

        {showJudges && (
          <aside className="w-72 border-l border-neutral-800 overflow-y-auto shrink-0 p-4" data-testid="judges-panel">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold mb-3">{t("rc.judgesQr")}</div>
            <div className="bg-white p-3 rounded-lg flex items-center justify-center" data-testid="judge-qr">
              <QRCodeCanvas value={judgeUrl} size={180} />
            </div>
            <p className="text-[10px] text-neutral-500 mt-2 break-all">{judgeUrl}</p>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm font-bold">{t("rc.ready")}</span>
              <span className={`font-monot tabular text-lg ${canStart ? "text-medical" : "text-warn"}`} data-testid="ready-count">{readyCount}/{numJudges}</span>
            </div>
            <div className="mt-3 space-y-2">
              {judges.length === 0 && <p className="text-xs text-neutral-500">{t("rc.noJudges")}</p>}
              {judges.map((j) => (
                <div key={j.judge_id} className={`flex items-center gap-2 text-sm ${j.online ? "" : "opacity-40"}`} data-testid={`judge-status-${j.judge_id}`}>
                  {j.ready && j.online ? <CheckCircle weight="fill" className="text-medical" size={16} /> : <Circle className={j.online ? "text-neutral-500" : "text-neutral-700"} size={16} />}
                  <span className="truncate">{j.name}</span>
                  {!j.online && <span className="text-[10px] uppercase tracking-wider text-neutral-600" data-testid={`judge-offline-${j.judge_id}`}>{t("rc.offline")}</span>}
                  <button onClick={() => removeJudge(j.judge_id)} title={t("rc.removeJudge")} className="ml-auto text-neutral-600 hover:text-hong shrink-0" data-testid={`remove-judge-${j.judge_id}`}><X size={14} weight="bold" /></button>
                </div>
              ))}
            </div>
            {judges.length > 0 && (
              <div className="mt-3 flex gap-4 justify-center">
                <button onClick={resetJudges} className="text-xs text-neutral-500 hover:text-warn underline underline-offset-2" data-testid="reset-judges-btn">{t("rc.resetJudges")}</button>
                <button onClick={clearJudges} className="text-xs text-neutral-500 hover:text-hong underline underline-offset-2" data-testid="clear-judges-btn">{t("rc.clearJudges")}</button>
              </div>
            )}
            <div className="mt-4 flex items-center justify-between border-t border-neutral-800 pt-3">
              <span className="text-xs">{t("rc.requireReady")}</span>
              <Switch checked={requireJudges} onCheckedChange={() => act("toggle_require_judges")} data-testid="require-judges-switch" />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function Side({ color, name, data, act, locked }) {
  const t = useT();
  const isRed = color === "red";
  const bg = isRed ? "bg-hong" : "bg-chung";
  return (
    <div className={`flex-1 ${bg} p-5 flex flex-col`} data-testid={`side-${color}`}>
      <div className="font-name text-3xl font-extrabold uppercase truncate">{name}</div>
      <div className="font-score text-8xl leading-none tabular mt-2" data-testid={`score-${color}`}>{data.score}</div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <Button disabled={locked} className="h-14 bg-black/25 hover:bg-black/40 text-white text-lg font-bold disabled:opacity-40" onClick={() => act("add_point", { side: color, amount: 1 })} data-testid={`add-1-${color}`}><Plus weight="bold" /> 1</Button>
        <Button disabled={locked} className="h-14 bg-black/25 hover:bg-black/40 text-white text-lg font-bold disabled:opacity-40" onClick={() => act("add_point", { side: color, amount: 2 })} data-testid={`add-2-${color}`}><Plus weight="bold" /> 2</Button>
        <Button disabled={locked} className="h-11 bg-black/20 hover:bg-black/40 text-white disabled:opacity-40" onClick={() => act("remove_point", { side: color, amount: 1 })} data-testid={`remove-point-${color}`}><Minus /> {t("rc.point")}</Button>
        <Button disabled={locked} className="h-11 bg-black/20 hover:bg-black/40 text-white disabled:opacity-40" onClick={() => act("set_winner", { winner: color })} data-testid={`winner-${color}`}><Trophy weight="fill" /> {t("rc.wins")}</Button>
      </div>
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between bg-black/20 rounded-md px-3 py-2">
          <span className="text-warn text-sm font-bold">{t("rc.warnLight")} <span data-testid={`warn-light-${color}`}>{data.warnings_light}</span></span>
          <div className="flex gap-1">
            <Button disabled={locked} size="icon" className="h-8 w-8 bg-warn/80 text-black hover:bg-warn disabled:opacity-40" onClick={() => act("add_warning", { side: color, type: "light" })} data-testid={`add-warn-light-${color}`}><Plus /></Button>
            <Button disabled={locked} size="icon" className="h-8 w-8 bg-black/30 disabled:opacity-40" onClick={() => act("remove_warning", { side: color, type: "light" })}><Minus /></Button>
          </div>
        </div>
        <div className="flex items-center justify-between bg-black/20 rounded-md px-3 py-2">
          <span className="text-sm font-bold">{t("rc.warnSevere")} <span data-testid={`warn-severe-${color}`}>{data.warnings_severe}</span></span>
          <div className="flex gap-1">
            <Button disabled={locked} size="icon" className="h-8 w-8 bg-black text-white hover:bg-neutral-900 border border-white/30 disabled:opacity-40" onClick={() => act("add_warning", { side: color, type: "severe" })} data-testid={`add-warn-severe-${color}`}><Plus /></Button>
            <Button disabled={locked} size="icon" className="h-8 w-8 bg-black/30 disabled:opacity-40" onClick={() => act("remove_warning", { side: color, type: "severe" })}><Minus /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CombatPanel({ match, remaining, running, act, canStart, readyCount, numJudges, requireJudges, soundOn, onToggleSound }) {
  const t = useT();
  const st = match.state;
  const locked = !!st.winner;
  const numRounds = match.config?.num_rounds ?? 2;
  const tied = (st.red?.score ?? 0) === (st.blue?.score ?? 0);
  const timeUp = remaining <= 0;
  const roundPlusDisabled = !((st.round ?? 1) >= numRounds && tied && timeUp);
  const resetMatch = () => { if (window.confirm(t("rc.resetMatchConfirm"))) act("reset_match"); };
  return (
    <div className="p-4">
      {st.ko_pending && !locked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" data-testid="ko-modal">
          <div className="w-full max-w-sm bg-card border border-gold/50 rounded-lg p-6 text-center space-y-4">
            <div className="text-gold font-heading font-extrabold text-2xl uppercase">{t("rc.koTitle")}</div>
            <p className="text-sm text-foreground">{t("rc.koMsg", { name: st.ko_pending === "red" ? nameOf(match, match.red_athlete_id) : nameOf(match, match.blue_athlete_id) })}</p>
            <div className="flex gap-2">
              <Button className="flex-1 h-11 font-bold gap-2" onClick={() => act("set_winner", { winner: st.ko_pending })} data-testid="ko-confirm-btn"><Trophy weight="fill" /> {t("rc.koConfirm")}</Button>
              <Button variant="outline" className="flex-1 h-11 font-bold border-neutral-700" onClick={() => act("dismiss_ko")} data-testid="ko-continue-btn">{t("rc.koContinue")}</Button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-center gap-4 mb-4">
        <div className="text-center">
          <div className="font-mono-t text-5xl font-bold tabular" data-testid="control-timer">{fmtClock(remaining)}</div>
          <div className="text-xs text-neutral-500 uppercase tracking-widest">{(st.phase || "round") === "break" ? t("rc.break") : `${t("rc.round")} ${st.round}`}</div>
        </div>
      </div>
      {!locked && (
        <div className="flex justify-center gap-2 mb-2 flex-wrap">
          {!running ? <Button className="gap-2 bg-medical text-black hover:bg-medical/90 font-bold disabled:opacity-40" onClick={() => act("start")} disabled={!canStart} data-testid="start-btn"><Play weight="fill" /> {t("rc.start")}</Button>
            : <Button className="gap-2 font-bold" variant="secondary" onClick={() => act("pause")} data-testid="pause-btn"><Pause weight="fill" /> {t("rc.pause")}</Button>}
          <Button variant="outline" className="gap-2 border-neutral-700" onClick={() => act("reset_timer")} data-testid="reset-btn"><ArrowClockwise /> {t("rc.reset")}</Button>
          <Button variant="outline" className="gap-2 border-neutral-700 disabled:opacity-40" disabled={roundPlusDisabled} title={roundPlusDisabled ? t("rc.roundExtraHint") : undefined} onClick={() => act("next_round")} data-testid="next-round-btn"><SkipForward /> {t("rc.roundPlus")}</Button>
          <Button variant="outline" className={`gap-2 border-neutral-700 ${st.medical ? "bg-medical text-black" : ""}`} onClick={() => act("medical")} data-testid="medical-btn"><FirstAid /> {t("rc.medical")}</Button>
          <Button variant="outline" size="icon" className={`border-neutral-700 ${soundOn ? "" : "text-neutral-500"}`} onClick={onToggleSound} title={soundOn ? t("rc.soundOn") : t("rc.soundOff")} data-testid="sound-toggle-btn">{soundOn ? <Microphone /> : <MicrophoneSlash />}</Button>
        </div>
      )}
      {!canStart && !locked && <div className="text-center mb-3 text-warn text-sm" data-testid="start-gate-msg">{t("rc.waitingJudges", { r: readyCount, n: numJudges })}</div>}
      {st.winner && <div className="text-center mb-4 text-gold font-bold" data-testid="winner-banner">{t("rc.winner", { name: st.winner === "red" ? nameOf(match, match.red_athlete_id) : nameOf(match, match.blue_athlete_id) })}</div>}
      <div className="flex gap-3">
        <Side color="red" name={nameOf(match, match.red_athlete_id)} data={st.red} act={act} locked={locked} />
        <Side color="blue" name={nameOf(match, match.blue_athlete_id)} data={st.blue} act={act} locked={locked} />
      </div>
      {locked && (
        <div className="mt-4 flex flex-col items-center gap-1">
          <Button className="w-full max-w-xs h-12 font-bold gap-2" onClick={resetMatch} data-testid="reset-match-btn"><ArrowClockwise weight="bold" /> {t("rc.resetMatch")}</Button>
          <span className="text-xs text-neutral-500">{t("rc.resetMatchHint")}</span>
        </div>
      )}
    </div>
  );
}

function ScoreChip({ color, name, avg, active, win }) {
  return (
    <div className={`px-3 py-2 rounded-lg border ${color === "red" ? "border-hong" : "border-chung"} ${active ? "bg-neutral-900 ring-2 " + (color === "red" ? "ring-hong" : "ring-chung") : ""} ${win ? "bg-gold/15" : ""}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${color === "red" ? "text-hong" : "text-chung"}`}>{color === "red" ? "HONG" : "CHUNG"}</div>
      <div className="font-name uppercase text-sm truncate max-w-[10rem]">{name}</div>
      <div className="font-score text-2xl text-gold tabular">{avg ?? "—"}</div>
    </div>
  );
}

function PoomsaePanel({ match, act, judges, canStart, poomsaeVotes }) {
  const t = useT();
  const st = match.state;
  const versus = st.mode === "versus";
  const side = st.current_side || "red";
  const activeId = versus ? (side === "red" ? match.red_athlete_id : match.blue_athlete_id) : (match.athlete_id || match.red_athlete_id);
  const forms = st.poomsae_scores.map((s) => s.form);
  const started = match.status === "live";
  const finished = match.status === "finished";
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="text-center mb-4">
        <div className="text-xs uppercase tracking-widest text-chung font-bold">{match.label}</div>
        {versus ? (
          <div className="flex items-center justify-center gap-3 mt-2">
            <ScoreChip color="red" name={nameOf(match, match.red_athlete_id)} avg={st.red?.poomsae_average} active={side === "red" && started && !finished} win={st.winner === "red"} />
            <span className="text-neutral-600 font-bold">VS</span>
            <ScoreChip color="blue" name={nameOf(match, match.blue_athlete_id)} avg={st.blue?.poomsae_average} active={side === "blue" && started && !finished} win={st.winner === "blue"} />
          </div>
        ) : (
          <div className="font-name text-4xl font-extrabold uppercase mt-1">{nameOf(match, activeId)}</div>
        )}
        {versus && started && !finished && (
          <div className="mt-2 text-sm font-bold" data-testid="poomsae-current-performer">{t("rc.performing")}: <span className={side === "red" ? "text-hong" : "text-chung"}>{nameOf(match, activeId)}</span></div>
        )}
      </div>

      {!started && !finished ? (
        <div className="text-center py-8">
          {!canStart && <p className="text-warn text-sm mb-3" data-testid="poomsae-gate-msg">{t("rc.waitingReady")}</p>}
          <Button className="h-12 px-8 font-bold bg-medical text-black hover:bg-medical/90 disabled:opacity-40" disabled={!canStart} onClick={() => act("start")} data-testid="poomsae-start">
            {t("rc.startPerf")}
          </Button>
          <p className="text-xs text-neutral-500 mt-3">{t("rc.startConsumes")}</p>
        </div>
      ) : finished && versus ? (
        <div className="text-center py-8" data-testid="poomsae-versus-result">
          <div className="text-gold font-bold text-xl">{st.winner ? t("rc.winner", { name: nameOf(match, st.winner === "red" ? match.red_athlete_id : match.blue_athlete_id) }) : t("rc.tie")}</div>
          <div className="mt-3 text-sm text-neutral-400">{nameOf(match, match.red_athlete_id)}: {st.red?.poomsae_average ?? "—"} · {nameOf(match, match.blue_athlete_id)}: {st.blue?.poomsae_average ?? "—"}</div>
          {st.winner == null && <p className="text-xs text-warn mt-2">{t("rc.tieHint")}</p>}
        </div>
      ) : (
        <>
          {forms.length > 1 && (
            <div className="flex justify-center gap-2 mb-5">
              {forms.map((f) => (
                <Button key={f} variant={st.current_form === f ? "default" : "outline"} className="border-neutral-700" onClick={() => act("set_current_form", { form: f })} data-testid={`select-form-${f}`}>
                  {t("rc.form")} {f}
                </Button>
              ))}
            </div>
          )}

          <div className="space-y-4">
            {st.poomsae_scores.map((s) => (
              <PoomsaeVotesTable key={s.form} s={s} act={act}
                liveVotes={poomsaeVotes && poomsaeVotes.form === s.form ? poomsaeVotes.votes : []} t={t} />
            ))}
          </div>

          <p className="text-xs text-neutral-500 mt-3">{t("rc.avgAuto")}</p>

          <div className="mt-6 flex items-center justify-between border-t border-neutral-800 pt-4">
            <span className="text-sm uppercase tracking-widest text-neutral-500">{t("rc.avgTotal")}</span>
            <span className="font-score text-6xl text-gold tabular" data-testid="poomsae-average">{st.poomsae_average ?? "—"}</span>
          </div>
        </>
      )}
    </div>
  );
}


function PoomsaeVotesTable({ s, liveVotes, t, act }) {
  const votes = (s.votes && s.votes.length) ? s.votes : (liveVotes || []);
  const fmt = (x) => (x == null ? "—" : Number(x).toFixed(1));
  const media = s.score != null
    ? s.score
    : (votes.length ? votes.reduce((a, v) => a + (v.total || 0), 0) / votes.length : null);
  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden" data-testid={`poomsae-form-${s.form}`}>
      <div className="px-3 py-2 bg-neutral-900 text-xs uppercase tracking-widest text-chung font-bold">{t("rc.form")} {s.form}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
              <th className="text-left px-3 py-2 font-semibold">{t("rc.colJudge")}</th>
              <th className="px-2 py-2 font-semibold">{t("rc.colTech")}</th>
              <th className="px-2 py-2 font-semibold">{t("rc.colBalance")}</th>
              <th className="px-2 py-2 font-semibold">{t("rc.colRhythm")}</th>
              <th className="px-2 py-2 font-semibold">{t("rc.colPower")}</th>
              <th className="px-2 py-2 font-semibold">{t("rc.colTotal")}</th>
              <th className="px-2 py-2 font-semibold border-l border-neutral-800">{t("rc.colAvg")}</th>
            </tr>
          </thead>
          <tbody>
            {votes.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-5 text-center text-neutral-600" data-testid={`poomsae-no-votes-${s.form}`}>{t("rc.noVotesYet")}</td></tr>
            )}
            {votes.map((v, i) => (
              <tr key={v.judge_id || i} className="border-b border-neutral-900" data-testid={`poomsae-vote-row-${s.form}-${i}`}>
                <td className="text-left px-3 py-2 truncate max-w-[9rem]">{v.name || `${t("rc.colJudge")} ${i + 1}`}</td>
                <td className="text-center px-2 py-2 tabular">{fmt(v.technical)}</td>
                <td className="text-center px-2 py-2 tabular">{fmt(v.balance)}</td>
                <td className="text-center px-2 py-2 tabular">{fmt(v.rhythm)}</td>
                <td className="text-center px-2 py-2 tabular">{fmt(v.energy)}</td>
                <td className="text-center px-2 py-2 tabular font-bold text-white">{fmt(v.total)}</td>
                {i === 0 && (
                  <td rowSpan={votes.length} className="text-center px-2 align-middle border-l border-neutral-800 font-score text-3xl text-gold tabular" data-testid={`poomsae-media-${s.form}`}>
                    {media != null ? Number(media).toFixed(2) : "—"}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col items-end gap-2 p-3 border-t border-neutral-800">
        {s.confirmed ? (
          <div className="w-1/2 h-9 rounded-md bg-medical text-black text-xs font-bold flex items-center justify-center gap-2" data-testid={`poomsae-confirmed-${s.form}`}>
            <CheckCircle weight="fill" size={16} /> {t("rc.perfConfirmed")}
          </div>
        ) : (
          <Button className="w-1/2 h-9 text-xs font-bold disabled:opacity-40" disabled={s.score == null} onClick={() => act("confirm_form", { form: s.form })} data-testid={`poomsae-confirm-${s.form}`}>{t("rc.confirmPerformance")}</Button>
        )}
        <Button variant="outline" size="sm" className="w-2/5 h-8 text-xs font-bold border-hong text-hong hover:bg-hong/10 disabled:opacity-40" disabled={s.score == null && !(s.votes && s.votes.length) && !s.confirmed} onClick={() => { if (window.confirm(t("rc.cancelPerfConfirm"))) act("cancel_form", { form: s.form }); }} data-testid={`poomsae-cancel-${s.form}`}>{t("rc.cancelPerformance")}</Button>
      </div>
    </div>
  );
}
